import type React from 'react';

import { feel } from './native-feel/index.js';

/**
 * A native-feeling bottom tab bar for the mobile PWAs.
 *
 * ## Why this exists
 *
 * The agent app's only mobile navigation was a hamburger in the header. That
 * is a web pattern: it hides every destination behind a tap, and an app whose
 * destinations are hidden reads as a website in a shell. A tab bar keeps the
 * whole map visible and puts it where a thumb already rests.
 *
 * It does NOT replace the header menu. The header comment in
 * `agent-pwa/src/components/app-shell.tsx` records a real constraint — brand
 * plus an appearance control plus the locale pill plus the hamburger measured
 * 312px on a 360px viewport and overflowed. This bar sits at the bottom and
 * competes for none of that space, so the two coexist: the bar carries the
 * primary destinations, the menu keeps settings and the long tail.
 *
 * ## The rules it follows
 *
 * · **At most five tabs.** Six 44px targets do not fit a 360px viewport with
 *   labels that survive translation into Tamil. The caller picks which five.
 * · **Phone only.** Hidden at 720px and up, where the header's own nav is
 *   already visible and a bottom bar would be a second navigation.
 * · **Safe-area aware.** `env(safe-area-inset-bottom)` keeps the row clear of
 *   the home indicator instead of sitting under it.
 * · **It reserves its own space.** The bar is fixed, so the page needs padding
 *   underneath or the last element hides behind it — `<BottomTabSpacer />`
 *   renders exactly that, and is the reason this file exports two things.
 *
 * Labels are passed in already translated: this component never sees a key,
 * and never hard-codes copy.
 */
export interface BottomTab {
  readonly to: string;
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly active: boolean;
}

export interface BottomTabBarProps {
  readonly tabs: readonly BottomTab[];
  /** Accessible name for the whole bar, e.g. "Main". Translated by the caller. */
  readonly ariaLabel: string;
  /**
   * Renders each tab. The apps use react-router's `NavLink`, which this package
   * deliberately does not depend on — `@kilat/ui` stays router-agnostic so it
   * can be rendered by any of the five apps.
   */
  readonly renderLink: (tab: BottomTab, children: React.ReactNode) => React.ReactNode;
}

/** Height of the bar itself, before the safe-area inset. */
const BAR_HEIGHT = 56;

export function BottomTabBar({ tabs, ariaLabel, renderLink }: BottomTabBarProps) {
  if (tabs.length === 0) return null;
  const shown = tabs.slice(0, 5);
  return (
    <nav
      aria-label={ariaLabel}
      data-bottom-tabs
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 30,
        // `display` is deliberately NOT set here. An inline style outranks any
        // selector, so `display: grid` inline would beat the `display: none`
        // that hides this bar at ≥720px — which is exactly what happened, and
        // is why `[data-bottom-tabs]` carries the grid in premium-motion.css
        // instead. Only the column count, which depends on the tab count,
        // stays inline.
        gridTemplateColumns: `repeat(${shown.length}, 1fr)`,
        background: 'var(--color-surface, #ffffff)',
        borderTop: '1px solid var(--color-border, rgba(15,23,42,0.08))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // A tab bar that scrolls with the page is not a tab bar.
        boxShadow: '0 -1px 2px var(--tint-subtle)',
      }}
    >
      {shown.map((tab) =>
        renderLink(
          tab,
          <span
            className="k-press"
            onPointerDown={() => feel('tap')}
            style={{
              minHeight: BAR_HEIGHT,
              display: 'grid',
              placeItems: 'center',
              gap: 2,
              padding: '6px 4px',
              color: tab.active
                ? 'var(--color-primary, #475569)'
                : 'var(--color-on-surface-muted, #64748b)',
              // Weight, not just colour: colour alone fails for the ~8% of men
              // with a red-green deficiency and washes out in bright sun, which
              // is where an agent actually uses this.
              fontWeight: tab.active ? 600 : 500,
              transition: 'color 140ms ease',
            }}
          >
            <span aria-hidden style={{ display: 'grid', placeItems: 'center' }}>
              {tab.icon}
            </span>
            <span
              style={{
                fontSize: 11,
                lineHeight: 1.1,
                textAlign: 'center',
                // Tamil and Malay labels run longer than English ones; wrapping
                // to two lines is correct, silently clipping is not.
                overflowWrap: 'anywhere',
              }}
            >
              {tab.label}
            </span>
          </span>,
        ),
      )}
    </nav>
  );
}

/**
 * Reserves the height the fixed bar occupies.
 *
 * Render it as the last child of the page. Without it the final row of content
 * sits underneath the bar — reachable only by over-scrolling, which on a
 * payout list means the last payout looks like it does not exist.
 */
export function BottomTabSpacer() {
  return (
    <div
      aria-hidden
      data-bottom-tab-spacer
      style={{ height: `calc(${BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))` }}
    />
  );
}
